import { ReactNode } from 'react'
import { Navigate } from 'react-router'
import { ValueOf } from 'type-fest'

import { pages } from '~/app/routes'
import { AstraPageContentLayout } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'

import NoAccess from '../NoAccess/NoAccess'
import { PageWrapper } from '../shared/PageWrapper'
import { Constraints } from './Constraints/Constraints'
import { TitleBar } from './Header/TitleBar'
import { Rooms } from './Rooms/Rooms'
import { Statuses } from './Statuses/Statuses'
import { Surgeons } from './Surgeons/Surgeons'

const hospitalConfigurationViews = pages.HOSPITAL_CONFIGURATION.views

type ViewDescriptor = {
    enabled: boolean
    component: ReactNode
    view: ValueOf<typeof hospitalConfigurationViews>
}

export const useEnabledHospitalConfigurationViews = () => {
    const isSurgeonsEnabled = useFlag('hospital_configuration_page__surgeons_enabled')
    const isRoomsEnabled = useFlag('hospital_configuration_page__rooms_enabled')
    const isStatusesEnabled = useFlag('hospital_configuration_page__statuses_enabled')

    const descriptors: ViewDescriptor[] = [
        {
            enabled: isSurgeonsEnabled,
            component: <Surgeons />,
            view: hospitalConfigurationViews.surgeons,
        },
        {
            enabled: isRoomsEnabled,
            component: <Rooms />,
            view: hospitalConfigurationViews.rooms,
        },
        {
            enabled: true,
            component: <Constraints />,
            view: hospitalConfigurationViews.constraints,
        },
        {
            enabled: isStatusesEnabled,
            component: <Statuses />,
            view: hospitalConfigurationViews.statuses,
        },
    ]

    return descriptors.filter(descriptor => descriptor.enabled)
}

export const NavigateToFirstEnabledHospitalConfigurationView = () => {
    const firstEnabledView = useEnabledHospitalConfigurationViews()[0]
    if (!firstEnabledView) {
        return <NoAccess />
    }
    return <Navigate to={firstEnabledView.view.path} replace />
}

const Content = () => {
    const activeView = useStore(state => state.app.activeViews.HOSPITAL_CONFIGURATION)
    const viewDescriptors = useEnabledHospitalConfigurationViews()

    const viewDescriptor = viewDescriptors.find(descriptor => descriptor.view.path === activeView)

    if (viewDescriptors.length === 0) {
        return <NoAccess />
    }
    // redirect to first enabled view
    if (!viewDescriptor) {
        return <Navigate to={viewDescriptors[0]?.view.path ?? '/'} replace />
    }

    return <PageWrapper>{viewDescriptor.component}</PageWrapper>
}

const HospitalConfigurationPage = () => <AstraPageContentLayout navigationHeader={<TitleBar />} content={<Content />} />

export default HospitalConfigurationPage
